import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { TRACK_LABELS, formatTime } from '@/lib/tracks';
import { History as HistoryIcon } from 'lucide-react';

export default function HistoryPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/test/history/list').then(({ data }) => {
      setRows(data.sessions || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <AppShell>
      <div className="space-y-5" data-testid="history-page">
        <div className="flex items-center gap-2">
          <HistoryIcon className="h-5 w-5" />
          <h1 className="font-display text-2xl font-semibold">Your test history</h1>
        </div>
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="space-y-3 p-8 text-center">
                <div className="font-display text-lg font-semibold">No tests yet</div>
                <div className="text-sm text-muted-foreground">Start your first chapter test now.</div>
                <Button onClick={() => nav('/dashboard')} data-testid="history-start-first-test">
                  Start now
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="history-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Track</TableHead>
                      <TableHead>Chapter</TableHead>
                      <TableHead>Set</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap">
                          {r.start_time ? new Date(r.start_time).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{TRACK_LABELS[r.track] || r.track}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[240px] truncate">
                          Class {r.klass} · {r.subject}
                          <div className="text-xs text-muted-foreground">{r.chapter}</div>
                        </TableCell>
                        <TableCell>{r.set_number}</TableCell>
                        <TableCell>
                          {r.status === 'submitted' ? (
                            <span className="font-mono-ui">
                              {r.correct}/{r.total} · {r.percentage}%
                            </span>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={r.status === 'submitted' ? 'default' : 'outline'} className="capitalize">
                            {r.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {r.status === 'submitted' ? (
                            <Button size="sm" variant="outline" onClick={() => nav(`/report/${r.id}`)} data-testid={`history-view-${r.id}`}>
                              View report
                            </Button>
                          ) : (
                            <Button size="sm" onClick={() => nav(`/test/${r.id}`)} data-testid={`history-resume-${r.id}`}>
                              Resume
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

// eslint-disable-next-line no-unused-vars
const _unused = formatTime;
